import { formatPhoneCountryCode } from '@shared/utils/config';

export interface FormattedPhone {
  formatted: string;
  telLink: string;
}

/**
 * Format any raw phone string into:
 * - formatted: human-readable with spaces
 * - telLink: a "tel:" link (no spaces, keeps '+' if present)
 *
 * Leading national trunk zero is removed when a country code is present.
 *
 * @param raw  Raw input (spaces, dashes, "+", etc.)
 * @returns FormattedPhone
 */
export const formatPhone = (raw: string): FormattedPhone => {
  // 1. Cleanup: keep only digits and a leading '+'
  const cleaned = raw.trim().replace(/(?!^\+)\D/g, '');

  // 2. Helper: dynamic grouping (odd→first single digit, then pairs; even→all pairs)
  const groupDigits = (digits: string): string[] => {
    const groups: string[] = [];
    const len = digits.length;
    let i = 0;

    if (len % 2 === 1) {
      groups.push(digits.charAt(0));
      i = 1;
    }
    for (; i < len; i += 2) {
      groups.push(digits.slice(i, i + 2));
    }
    return groups;
  };

  // 3. Copy & sort without mutating original
  const sortedCodes: string[] = [...formatPhoneCountryCode].sort((a, b) => b.length - a.length);

  // 4. International branch: detect CC and subscriber
  if (cleaned.startsWith('+')) {
    const digitsOnly = cleaned.slice(1);
    let cc = '';
    for (const code of sortedCodes) {
      if (digitsOnly.startsWith(code)) {
        cc = code;
        break;
      }
    }

    if (cc) {
      let subscriber = digitsOnly.slice(cc.length);
      if (subscriber.startsWith('0')) {
        subscriber = subscriber.slice(1);
      }
      const subsGroups = groupDigits(subscriber);
      const formatted = `+${cc}` + (subsGroups.length ? ' ' + subsGroups.join(' ') : '');
      const telLink = `tel:+${cc}${subscriber}`;
      return { formatted, telLink };
    }
  }

  // 5. National formatting
  const natDigits = cleaned;
  const natGroups = groupDigits(natDigits);
  const formatted = natGroups.join(' ');
  const telLink = `tel:${natDigits}`;

  return { formatted, telLink };
};
